import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/app/AppShell";
import LogoTile from "@/components/app/LogoTile";
import SignaturePad from "@/components/app/SignaturePad";
import { useIsMobile } from "@/hooks/useMediaQuery";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAYS_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const ROWS = ["REGULAR", "OT", "DOUBLE"];
const ROW_KEYS = ["regular", "ot", "double"];

const fmtMD = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso + "T00:00:00");
        return `${d.getMonth() + 1}/${d.getDate()}`;
    } catch { return iso; }
};

const num = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const f = parseFloat(v);
    if (Number.isNaN(f) || f < 0) return null;
    return f;
};

const sum7 = (arr) => (arr || []).reduce((a, b) => a + (num(b) || 0), 0);

export default function TimesheetEditor({ readOnly: forceReadOnly = false }) {
    const { id } = useParams();
    const nav = useNavigate();
    const isMobile = useIsMobile();
    const [doc, setDoc] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [suggestions, setSuggestions] = useState({ recent_job_names: [], recent_job_numbers: [] });
    const [activeDay, setActiveDay] = useState(0);
    const [submitErrors, setSubmitErrors] = useState(null);
    const dayRefs = useRef([]);
    const patchTimer = useRef(null);

    const readOnly = forceReadOnly || (doc && doc.status !== "draft");

    // Load doc + suggestions
    useEffect(() => {
        (async () => {
            try {
                const [d, s] = await Promise.all([
                    api.get(`/timesheets/${id}`),
                    api.get(`/me/suggestions`).catch(() => ({ data: {} })),
                ]);
                setDoc(d.data);
                setSuggestions({
                    recent_job_names: s.data.recent_job_names || [],
                    recent_job_numbers: s.data.recent_job_numbers || [],
                });
            } catch (err) {
                if (err?.response?.status === 404) {
                    nav("/reports?tab=timesheets", { replace: true });
                    return;
                }
                setError(formatApiError(err));
            }
        })();
    }, [id, nav]);

    // Debounced PATCH
    const schedulePatch = useCallback(async (payload) => {
        if (readOnly) return;
        if (patchTimer.current) clearTimeout(patchTimer.current);
        patchTimer.current = setTimeout(async () => {
            try {
                const { data } = await api.patch(`/timesheets/${id}`, payload);
                setDoc(data);
                setSavedAt(new Date());
            } catch (err) {
                setError(formatApiError(err));
            }
        }, 500);
    }, [id, readOnly]);

    const updateJobField = (jobIdx, key, value) => {
        setDoc((prev) => {
            const jobs = prev.jobs.map((j, i) => i === jobIdx ? { ...j, [key]: value } : j);
            const next = { ...prev, jobs };
            schedulePatch({ jobs });
            return next;
        });
    };

    const updateCell = (jobIdx, rowIdx, dayIdx, value) => {
        setDoc((prev) => {
            const jobs = prev.jobs.map((j, i) => {
                if (i !== jobIdx) return j;
                const arr = [...(j[ROW_KEYS[rowIdx]] || [])];
                while (arr.length < 7) arr.push(null);
                arr[dayIdx] = value === "" ? null : num(value);
                return { ...j, [ROW_KEYS[rowIdx]]: arr };
            });
            const next = { ...prev, jobs };
            schedulePatch({ jobs });
            return next;
        });
    };

    const updateScale = (jobIdx, rowIdx, value) => {
        setDoc((prev) => {
            const jobs = prev.jobs.map((j, i) => {
                if (i !== jobIdx) return j;
                const arr = [...(j.scale || [null, null, null])];
                while (arr.length < 3) arr.push(null);
                arr[rowIdx] = value || null;
                return { ...j, scale: arr };
            });
            const next = { ...prev, jobs };
            schedulePatch({ jobs });
            return next;
        });
    };

    const updateTop = (payload) => {
        setDoc((prev) => ({ ...prev, ...payload }));
        schedulePatch(payload);
    };

    const forceSave = async () => {
        if (patchTimer.current) clearTimeout(patchTimer.current);
        setBusy(true);
        try {
            const { data } = await api.patch(`/timesheets/${id}`, {
                employee_name: doc.employee_name,
                week_ending: doc.week_ending,
                jobs: doc.jobs,
                epoxy_hours: doc.epoxy_hours,
                miles_driven: doc.miles_driven,
                miles_driven_scale: doc.miles_driven_scale,
                receipts_total: doc.receipts_total,
                receipts_scale: doc.receipts_scale,
                signed_png_base64: doc.signed_png_base64,
            });
            setDoc(data);
            setSavedAt(new Date());
            toast.success("Saved", { duration: 1200 });
        } catch (err) {
            toast.error(formatApiError(err), { duration: 2500 });
        } finally {
            setBusy(false);
        }
    };

    const doDelete = async () => {
        if (!confirm("Delete this draft timesheet?")) return;
        try {
            await api.delete(`/timesheets/${id}`);
            nav("/reports?tab=timesheets", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err), { duration: 2500 });
        }
    };

    const submit = async () => {
        setSubmitErrors(null);
        setBusy(true);
        try {
            if (patchTimer.current) clearTimeout(patchTimer.current);
            // ensure latest is persisted before submit
            await api.patch(`/timesheets/${id}`, {
                employee_name: doc.employee_name,
                week_ending: doc.week_ending,
                jobs: doc.jobs,
                epoxy_hours: doc.epoxy_hours,
                miles_driven: doc.miles_driven,
                miles_driven_scale: doc.miles_driven_scale,
                receipts_total: doc.receipts_total,
                receipts_scale: doc.receipts_scale,
                signed_png_base64: doc.signed_png_base64,
            });
            const { data } = await api.post(`/timesheets/${id}/submit`, {});
            setDoc(data);
            toast.success("Submitted", { duration: 1500 });
            nav(`/timesheets/${id}`, { replace: true });
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (d && typeof d === "object" && d.errors) {
                setSubmitErrors(d.errors);
                toast.error("Fix the highlighted fields before submitting", { duration: 2500 });
            } else {
                toast.error(formatApiError(err), { duration: 2500 });
            }
        } finally {
            setBusy(false);
        }
    };

    if (error) {
        return (
            <AppShell>
                <div className="glass-card">
                    <div className="err">{error}</div>
                    <Link to="/reports?tab=timesheets" className="btn btn-secondary mt-3">← Back</Link>
                </div>
            </AppShell>
        );
    }
    if (!doc) return <AppShell><div className="glass-card text-[13px] text-slate-500">Loading…</div></AppShell>;

    const weekDays = doc.week_days || [];
    const submitted = doc.status !== "draft";

    return (
        <AppShell>
            <div className="flex items-center justify-between mb-4">
                <Link to="/reports?tab=timesheets" className="btn btn-secondary btn-sm" data-testid="ts-back">
                    <ArrowLeft size={14} strokeWidth={2} />
                    Back
                </Link>
                <div className="text-[12px] text-slate-500">
                    {submitted ? (
                        <span className="chip chip-certified" data-testid="ts-status-chip">Submitted</span>
                    ) : savedAt ? (
                        <>Saved <span className="tabular-nums">{savedAt.toLocaleTimeString()}</span></>
                    ) : (
                        <>Autosaves on change</>
                    )}
                </div>
            </div>

            {submitted && (
                <div className="glass-card !py-3 mb-4 flex items-center gap-3 border border-amber-200" data-testid="ts-locked-banner">
                    <span className="chip chip-certified">Locked</span>
                    <span className="text-[13px] text-slate-700">
                        Submitted on {new Date(doc.submitted_at).toLocaleString()} — this timesheet is read-only.
                    </span>
                </div>
            )}

            {/* Header card */}
            <div className="glass-card mb-4">
                <div className="flex items-center gap-3 mb-4">
                    <LogoTile size={44} />
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-slate-400">Timesheet</div>
                        <div className="text-[18px] font-semibold text-slate-900">Weekly hours by job</div>
                    </div>
                </div>
                <div className="flex flex-col md:flex-row gap-3">
                    <div className="md:flex-1 min-w-0">
                        <label className="label" htmlFor="ts-employee">Employee name</label>
                        <input
                            id="ts-employee"
                            className="input"
                            value={doc.employee_name || ""}
                            onChange={(e) => updateTop({ employee_name: e.target.value })}
                            disabled={readOnly}
                            data-testid="ts-employee-input"
                        />
                    </div>
                    <div className="md:w-[240px] shrink-0">
                        <label className="label" htmlFor="ts-week">Week ending (Sunday)</label>
                        <input
                            id="ts-week"
                            type="date"
                            className="input"
                            value={doc.week_ending || ""}
                            onChange={(e) => updateTop({ week_ending: e.target.value })}
                            disabled={readOnly}
                            data-testid="ts-week-input"
                        />
                    </div>
                </div>
                {submitErrors && (submitErrors.employee_name || submitErrors.week_ending) && (
                    <div className="err mt-2">
                        {submitErrors.employee_name || submitErrors.week_ending}
                    </div>
                )}
            </div>

            <datalist id="dl-job-names">
                {suggestions.recent_job_names.map((n) => <option key={n} value={n} />)}
            </datalist>
            <datalist id="dl-job-numbers">
                {suggestions.recent_job_numbers.map((n) => <option key={n} value={n} />)}
            </datalist>

            {/* Desktop grid */}
            {!isMobile && (
                <div className="glass-card !p-0 overflow-hidden mb-4 desktop-only">
                    <div className="overflow-x-auto">
                        <table className="ts-grid" data-testid="ts-desktop-grid">
                            <thead>
                                <tr>
                                    <th className="ts-rail-h">DAY / HOURS</th>
                                    {weekDays.map((wd, i) => (
                                        <th key={i} className="ts-day-h">
                                            <div className="tabular-nums text-[13px] text-slate-900">{fmtMD(wd)}</div>
                                            <div className="text-[10px] uppercase tracking-wider text-slate-500">{DAYS[i]}</div>
                                        </th>
                                    ))}
                                    <th className="ts-day-h">TOTAL</th>
                                    <th className="ts-day-h" style={{ width: 110 }}>SCALE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {doc.jobs.map((job, jIdx) => (
                                    <>
                                        <tr key={`jhead-${jIdx}`} className="ts-jobhead">
                                            <td className="ts-rail">
                                                <div className="flex flex-col gap-1.5">
                                                    <input
                                                        className="input !h-8 !py-1 text-[13px]"
                                                        placeholder="Job name"
                                                        value={job.job_name || ""}
                                                        onChange={(e) => updateJobField(jIdx, "job_name", e.target.value)}
                                                        list="dl-job-names"
                                                        disabled={readOnly}
                                                        data-testid={`ts-job-name-${jIdx}`}
                                                    />
                                                    <input
                                                        className="input !h-8 !py-1 text-[13px] tabular-nums"
                                                        placeholder="Job #"
                                                        value={job.job_number || ""}
                                                        onChange={(e) => updateJobField(jIdx, "job_number", e.target.value)}
                                                        list="dl-job-numbers"
                                                        disabled={readOnly}
                                                        data-testid={`ts-job-number-${jIdx}`}
                                                    />
                                                </div>
                                            </td>
                                            <td colSpan={9} className="ts-jobhead-strip" />
                                        </tr>
                                        {ROWS.map((rowLabel, rIdx) => {
                                            const arr = job[ROW_KEYS[rIdx]] || [];
                                            const rowTotal = sum7(arr);
                                            return (
                                                <tr key={`row-${jIdx}-${rIdx}`}>
                                                    <td className="ts-rowlabel">{rowLabel}</td>
                                                    {Array.from({ length: 7 }, (_, dIdx) => (
                                                        <td key={dIdx} className="ts-cell">
                                                            <input
                                                                className="ts-input"
                                                                inputMode="decimal"
                                                                type="number"
                                                                step="0.25"
                                                                min="0"
                                                                value={arr[dIdx] ?? ""}
                                                                onChange={(e) => updateCell(jIdx, rIdx, dIdx, e.target.value)}
                                                                disabled={readOnly}
                                                                data-testid={`ts-cell-${jIdx}-${rIdx}-${dIdx}`}
                                                            />
                                                        </td>
                                                    ))}
                                                    <td className="ts-cell ts-total tabular-nums" data-testid={`ts-rowtotal-${jIdx}-${rIdx}`}>
                                                        {rowTotal > 0 ? rowTotal : ""}
                                                    </td>
                                                    <td className="ts-cell">
                                                        <input
                                                            className="ts-input"
                                                            value={(job.scale || [])[rIdx] || ""}
                                                            onChange={(e) => updateScale(jIdx, rIdx, e.target.value)}
                                                            disabled={readOnly}
                                                            data-testid={`ts-scale-${jIdx}-${rIdx}`}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Mobile day-pill nav + per-day sections */}
            {isMobile && (
                <div className="mb-4 mobile-only">
                    <div className="glass-card !p-3 overflow-hidden mb-3">
                        <div className="flex gap-2 overflow-x-auto" data-testid="ts-day-pills">
                            {DAYS.map((d, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => {
                                        setActiveDay(i);
                                        dayRefs.current[i]?.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }}
                                    className="tab-pill shrink-0"
                                    data-active={activeDay === i || undefined}
                                    data-testid={`ts-day-pill-${i}`}
                                >
                                    <div className="text-[11px] uppercase tracking-wider">{DAYS[i]}</div>
                                    <div className="text-[11px] tabular-nums text-slate-500">{fmtMD(weekDays[i])}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                    {DAYS.map((_, dIdx) => (
                        <div
                            key={dIdx}
                            ref={(el) => { dayRefs.current[dIdx] = el; }}
                            className="glass-card mb-3"
                            data-testid={`ts-day-section-${dIdx}`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-slate-400">Day</div>
                                    <div className="text-[16px] font-semibold text-slate-900">
                                        {DAYS_LONG[dIdx]} · <span className="tabular-nums">{fmtMD(weekDays[dIdx])}</span>
                                    </div>
                                </div>
                            </div>
                            {doc.jobs.map((job, jIdx) => (
                                <div key={jIdx} className="border-t border-[var(--divider)] pt-3 mt-3 first:mt-0 first:border-t-0 first:pt-0">
                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                        <input
                                            className="input"
                                            placeholder="Job name"
                                            value={job.job_name || ""}
                                            onChange={(e) => updateJobField(jIdx, "job_name", e.target.value)}
                                            list="dl-job-names"
                                            disabled={readOnly}
                                            data-testid={`ts-m-job-name-${dIdx}-${jIdx}`}
                                        />
                                        <input
                                            className="input tabular-nums"
                                            placeholder="Job #"
                                            value={job.job_number || ""}
                                            onChange={(e) => updateJobField(jIdx, "job_number", e.target.value)}
                                            list="dl-job-numbers"
                                            disabled={readOnly}
                                        />
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {ROWS.map((rowLabel, rIdx) => (
                                            <div key={rIdx}>
                                                <label className="label !text-[10px]">{rowLabel === "REGULAR" ? "Reg" : rowLabel === "OT" ? "OT" : "Dbl"}</label>
                                                <input
                                                    className="input tabular-nums text-right"
                                                    inputMode="decimal"
                                                    type="number"
                                                    step="0.25"
                                                    min="0"
                                                    value={(job[ROW_KEYS[rIdx]] || [])[dIdx] ?? ""}
                                                    onChange={(e) => updateCell(jIdx, rIdx, dIdx, e.target.value)}
                                                    disabled={readOnly}
                                                    data-testid={`ts-m-cell-${dIdx}-${jIdx}-${rIdx}`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}

            {submitErrors?.hours && (
                <div className="err mb-3">{submitErrors.hours}</div>
            )}
            {submitErrors?.jobs && (
                <div className="err mb-3">{submitErrors.jobs}</div>
            )}

            {/* Totals card */}
            <div className="glass-card mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-slate-400">Total</div>
                        <div className="text-[22px] font-semibold text-slate-900 tabular-nums" data-testid="ts-total-regular">
                            {doc.total_regular ?? 0}
                        </div>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-slate-400">Total OT (1½×)</div>
                        <div className="text-[22px] font-semibold text-slate-900 tabular-nums" data-testid="ts-total-ot">
                            {doc.total_ot ?? 0}
                        </div>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-slate-400">Total DT (2×)</div>
                        <div className="text-[22px] font-semibold text-slate-900 tabular-nums" data-testid="ts-total-double">
                            {doc.total_double ?? 0}
                        </div>
                    </div>
                    <div>
                        <label className="label" htmlFor="ts-epoxy">Epoxy hours</label>
                        <input
                            id="ts-epoxy"
                            type="number"
                            inputMode="decimal"
                            step="0.25"
                            className="input tabular-nums"
                            value={doc.epoxy_hours ?? ""}
                            onChange={(e) => updateTop({ epoxy_hours: num(e.target.value) })}
                            disabled={readOnly}
                            data-testid="ts-epoxy-input"
                        />
                    </div>
                </div>
            </div>

            {/* Miles + Receipts */}
            <div className="glass-card mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                    <div>
                        <label className="label">Miles driven</label>
                        <div className="grid grid-cols-3 gap-2">
                            <input
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                className="input tabular-nums col-span-1"
                                value={doc.miles_driven ?? ""}
                                onChange={(e) => updateTop({ miles_driven: num(e.target.value) })}
                                disabled={readOnly}
                                data-testid="ts-miles-input"
                            />
                            <input
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                className="input tabular-nums col-span-1"
                                placeholder="S %"
                                value={doc.miles_driven_scale?.s_pct ?? ""}
                                onChange={(e) => updateTop({ miles_driven_scale: { ...(doc.miles_driven_scale || {}), s_pct: num(e.target.value) } })}
                                disabled={readOnly}
                            />
                            <input
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                className="input tabular-nums col-span-1"
                                placeholder="F %"
                                value={doc.miles_driven_scale?.f_pct ?? ""}
                                onChange={(e) => updateTop({ miles_driven_scale: { ...(doc.miles_driven_scale || {}), f_pct: num(e.target.value) } })}
                                disabled={readOnly}
                            />
                        </div>
                        <div className="hint mt-1">See original template for definition of S % / F %.</div>
                    </div>
                    <div>
                        <label className="label">Receipts total</label>
                        <div className="grid grid-cols-3 gap-2">
                            <input
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                className="input tabular-nums col-span-1"
                                value={doc.receipts_total ?? ""}
                                onChange={(e) => updateTop({ receipts_total: num(e.target.value) })}
                                disabled={readOnly}
                                data-testid="ts-receipts-input"
                            />
                            <input
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                className="input tabular-nums col-span-1"
                                placeholder="S %"
                                value={doc.receipts_scale?.s_pct ?? ""}
                                onChange={(e) => updateTop({ receipts_scale: { ...(doc.receipts_scale || {}), s_pct: num(e.target.value) } })}
                                disabled={readOnly}
                            />
                            <input
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                className="input tabular-nums col-span-1"
                                placeholder="F %"
                                value={doc.receipts_scale?.f_pct ?? ""}
                                onChange={(e) => updateTop({ receipts_scale: { ...(doc.receipts_scale || {}), f_pct: num(e.target.value) } })}
                                disabled={readOnly}
                            />
                        </div>
                        <div className="hint mt-1">See original template for definition of S % / F %.</div>
                    </div>
                </div>
            </div>

            {/* Signatures */}
            <div className="glass-card mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Signed</div>
                        {readOnly ? (
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-10" aria-hidden>
                                    <span className="stamp-certified" style={{ color: "#B45309" }}>Submitted</span>
                                </div>
                                {doc.signed_png_base64 ? (
                                    <img src={`data:image/png;base64,${doc.signed_png_base64}`} alt="Signature" className="w-full h-[140px] object-contain bg-white rounded border border-slate-200" />
                                ) : (
                                    <div className="h-[140px] rounded border border-slate-200 bg-slate-50" />
                                )}
                            </div>
                        ) : (
                            <SignaturePad
                                initialDataUrl={doc.signed_png_base64 ? `data:image/png;base64,${doc.signed_png_base64}` : undefined}
                                onEnd={(dataUrl) => {
                                    // dataUrl is a data: url — strip the prefix, store only the b64 payload
                                    const b64 = (dataUrl || "").split(",")[1] || "";
                                    updateTop({ signed_png_base64: b64 });
                                }}
                                data-testid="ts-signature-pad"
                            />
                        )}
                        {submitErrors?.signed_png_base64 && (
                            <div className="err mt-2">{submitErrors.signed_png_base64}</div>
                        )}
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Approved</div>
                        {doc.approved_png_base64 ? (
                            <img src={`data:image/png;base64,${doc.approved_png_base64}`} alt="Approved signature" className="w-full h-[140px] object-contain bg-white rounded border border-slate-200" />
                        ) : (
                            <div className="h-[140px] rounded border border-dashed border-slate-300 bg-white/50 flex items-center justify-center text-[13px] text-slate-400">
                                Awaiting approval
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer actions — desktop inline / mobile sticky */}
            {!readOnly && (
                <>
                    <div className="hidden md:flex items-center justify-between gap-3 mt-6" data-testid="ts-desktop-actions">
                        <button
                            type="button"
                            onClick={doDelete}
                            className="btn btn-danger"
                            data-testid="ts-delete-button"
                        >
                            <Trash2 size={14} strokeWidth={2} />
                            Delete draft
                        </button>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={forceSave}
                                disabled={busy}
                                className="btn btn-secondary"
                                data-testid="ts-save-button"
                            >
                                <Save size={14} strokeWidth={2} />
                                Save as draft
                            </button>
                            <button
                                type="button"
                                onClick={submit}
                                disabled={busy}
                                className="btn btn-primary"
                                data-testid="ts-submit-button"
                            >
                                <Check size={14} strokeWidth={2.5} />
                                Submit
                            </button>
                        </div>
                    </div>
                    <div
                        className="mobile-only fixed left-0 right-0 bottom-0 z-30 px-3 pt-2 pb-3 bg-white/85 border-t border-[var(--divider)] backdrop-blur"
                        style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}
                        data-testid="ts-mobile-actions"
                    >
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={forceSave}
                                disabled={busy}
                                className="btn btn-secondary flex-1"
                            >
                                <Save size={14} strokeWidth={2} />
                                Save
                            </button>
                            <button
                                type="button"
                                onClick={submit}
                                disabled={busy}
                                className="btn btn-primary flex-1"
                            >
                                <Check size={14} strokeWidth={2.5} />
                                Submit
                            </button>
                        </div>
                    </div>
                    {/* Spacer so sticky bar doesn't overlap last card on mobile */}
                    <div className="mobile-only" style={{ height: 96 }} />
                </>
            )}
        </AppShell>
    );
}
